import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft, Plus, Calendar, FileText, Eye, Glasses, Pill, 
  Printer, Trash2, ChevronDown, ChevronUp, Search, X, UserPlus, Stethoscope,
  Mail, MessageCircle, Share2, MoreVertical
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Badge } from '../components/ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;

// Helper to calculate age
const calculateAge = (birthDate) => {
  if (!birthDate) return null;
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
};

const PatientEvaluationsPage = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  
  const [patient, setPatient] = useState(null);
  const [evaluations, setEvaluations] = useState([]);
  const [diagnoses, setDiagnoses] = useState([]);
  const [surgeryTypes, setSurgeryTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedEval, setExpandedEval] = useState(null);
  const [savingEval, setSavingEval] = useState(false);
  const [lastCreatedEvalId, setLastCreatedEvalId] = useState(null);
  
  // New evaluation modal
  const [showNewEvalModal, setShowNewEvalModal] = useState(false);
  const [newEval, setNewEval] = useState({
    fecha: new Date().toISOString().split('T')[0],
    motivo_consulta: '',
    hallazgos: '',
    tratamiento: '',
    diagnostico_ids: [],
    notas: '',
    agregar_crm_cirugia: false,
    tipo_cirugia_id: '',
    nivel_interes: 'medio'
  });
  
  // Diagnosis search/create
  const [diagnosisSearch, setDiagnosisSearch] = useState('');
  const [showNewDiagnosisInput, setShowNewDiagnosisInput] = useState(false);
  const [newDiagnosis, setNewDiagnosis] = useState({ name: '', category: '' });
  
  // Optical prescription modal
  const [showOpticalModal, setShowOpticalModal] = useState(false);
  const [currentEvalForRx, setCurrentEvalForRx] = useState(null);
  const [opticalRx, setOpticalRx] = useState({
    tipo: 'anteojos',
    ojo_derecho: { esfera: '', cilindro: '', eje: '', adicion: '', dp: '', av_sin_correccion: '', av_con_correccion: '' },
    ojo_izquierdo: { esfera: '', cilindro: '', eje: '', adicion: '', dp: '', av_sin_correccion: '', av_con_correccion: '' },
    tipo_lente: '',
    material: '',
    tratamientos: '',
    observaciones: ''
  });
  
  // Medical prescription modal
  const [showMedicalModal, setShowMedicalModal] = useState(false);
  const [medicalRx, setMedicalRx] = useState({
    medicamentos: [{ nombre: '', componente_activo: '', modo_uso: '', observaciones: '' }],
    proxima_cita_fecha: '',
    proxima_cita_hora: '',
    proxima_cita_tipo: 'control',
    observaciones_generales: ''
  });
  
  // Print preview
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [printData, setPrintData] = useState(null);
  const [printType, setPrintType] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [historyRes, diagnosesRes, surgeryTypesRes] = await Promise.all([
        axios.get(`${API}/api/evaluations/patient/${patientId}/history`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/evaluations/diagnoses`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/surgery-crm/surgery-types`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setPatient(historyRes.data.patient);
      setEvaluations(historyRes.data.evaluations || []);
      setDiagnoses(diagnosesRes.data || []);
      setSurgeryTypes(surgeryTypesRes.data || []);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [patientId, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateEvaluation = async () => {
    if (!newEval.motivo_consulta.trim()) {
      toast.error('El motivo de consulta es requerido');
      return;
    }
    
    setSavingEval(true);
    try {
      // Create evaluation
      const evalRes = await axios.post(`${API}/api/evaluations`, {
        patient_id: patientId,
        fecha: newEval.fecha,
        motivo_consulta: newEval.motivo_consulta + (newEval.hallazgos ? `\n\nHallazgos: ${newEval.hallazgos}` : ''),
        tratamiento: newEval.tratamiento,
        diagnostico_ids: newEval.diagnostico_ids,
        notas: newEval.notas
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const createdEvalId = evalRes.data.id;
      setLastCreatedEvalId(createdEvalId);
      
      // Add to surgery CRM if checked
      if (newEval.agregar_crm_cirugia && newEval.tipo_cirugia_id) {
        try {
          await axios.post(`${API}/api/surgery-crm/interested`, {
            paciente_id: patientId,
            nombre_completo: patient.full_name,
            telefono: patient.phone,
            correo: patient.email || '',
            tipo_cirugia_id: newEval.tipo_cirugia_id,
            nivel_interes: newEval.nivel_interes,
            observaciones_generales: `Agregado desde evaluación del ${newEval.fecha}`
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          toast.success('Paciente agregado al CRM de cirugías');
        } catch (crmError) {
          console.error('Error adding to CRM:', crmError);
          toast.error('No se pudo agregar al CRM de cirugías');
        }
      }
      
      toast.success('Evaluación creada');
      
      // Set current eval for prescription creation
      setCurrentEvalForRx({ id: createdEvalId, fecha: newEval.fecha });
      
      // Reset form
      setNewEval({
        fecha: new Date().toISOString().split('T')[0],
        motivo_consulta: '',
        hallazgos: '',
        tratamiento: '',
        diagnostico_ids: [],
        notas: '',
        agregar_crm_cirugia: false,
        tipo_cirugia_id: '',
        nivel_interes: 'medio'
      });
      
      setShowNewEvalModal(false);
      fetchData();
      
    } catch (error) {
      toast.error('Error al crear evaluación');
    } finally {
      setSavingEval(false);
    }
  };

  const handleCreateDiagnosis = async () => {
    if (!newDiagnosis.name.trim()) {
      toast.error('El nombre del diagnóstico es requerido');
      return;
    }
    
    try {
      const res = await axios.post(`${API}/api/evaluations/diagnoses`, newDiagnosis, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDiagnoses([...diagnoses, res.data]);
      setNewEval({
        ...newEval,
        diagnostico_ids: [...newEval.diagnostico_ids, res.data.id]
      });
      setNewDiagnosis({ name: '', category: '' });
      setShowNewDiagnosisInput(false);
      toast.success('Diagnóstico creado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear diagnóstico');
    }
  };

  const handleDeleteEvaluation = async (evalId) => {
    if (!window.confirm('¿Eliminar esta evaluación y todas sus recetas?')) return;
    
    try {
      await axios.delete(`${API}/api/evaluations/${evalId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Evaluación eliminada');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleCreateOpticalRx = async () => {
    try {
      await axios.post(`${API}/api/evaluations/optical-prescriptions`, {
        evaluacion_id: currentEvalForRx.id,
        patient_id: patientId,
        ...opticalRx
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Receta óptica creada');
      setShowOpticalModal(false);
      resetOpticalRx();
      fetchData();
    } catch (error) {
      toast.error('Error al crear receta');
    }
  };

  const handleCreateMedicalRx = async () => {
    const validMeds = medicalRx.medicamentos.filter(m => m.nombre.trim());
    if (validMeds.length === 0) {
      toast.error('Agregue al menos un medicamento');
      return;
    }
    
    try {
      await axios.post(`${API}/api/evaluations/medical-prescriptions`, {
        evaluacion_id: currentEvalForRx.id,
        patient_id: patientId,
        ...medicalRx,
        medicamentos: validMeds
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Receta médica creada');
      setShowMedicalModal(false);
      resetMedicalRx();
      fetchData();
    } catch (error) {
      toast.error('Error al crear receta');
    }
  };

  const resetOpticalRx = () => {
    setOpticalRx({
      tipo: 'anteojos',
      ojo_derecho: { esfera: '', cilindro: '', eje: '', adicion: '', dp: '', av_sin_correccion: '', av_con_correccion: '' },
      ojo_izquierdo: { esfera: '', cilindro: '', eje: '', adicion: '', dp: '', av_sin_correccion: '', av_con_correccion: '' },
      tipo_lente: '',
      material: '',
      tratamientos: '',
      observaciones: ''
    });
  };

  const resetMedicalRx = () => {
    setMedicalRx({
      medicamentos: [{ nombre: '', componente_activo: '', modo_uso: '', observaciones: '' }],
      proxima_cita_fecha: '',
      proxima_cita_hora: '',
      proxima_cita_tipo: 'control',
      observaciones_generales: ''
    });
  };

  const addMedicamento = () => {
    setMedicalRx({
      ...medicalRx,
      medicamentos: [...medicalRx.medicamentos, { nombre: '', componente_activo: '', modo_uso: '', observaciones: '' }]
    });
  };

  const removeMedicamento = (index) => {
    setMedicalRx({
      ...medicalRx,
      medicamentos: medicalRx.medicamentos.filter((_, i) => i !== index)
    });
  };

  const updateMedicamento = (index, field, value) => {
    const updated = [...medicalRx.medicamentos];
    updated[index][field] = value;
    setMedicalRx({ ...medicalRx, medicamentos: updated });
  };

  const handlePrint = (data, type) => {
    setPrintData(data);
    setPrintType(type);
    setShowPrintPreview(true);
  };

  const executePrint = () => {
    window.print();
  };

  // Generate text for WhatsApp/Email
  const generatePrescriptionText = (data, type) => {
    const formatDate = (dateStr) => {
      return new Date(dateStr + 'T12:00:00').toLocaleDateString('es-GT', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    };

    if (type === 'optical') {
      let text = `*RECETA ÓPTICA*\n`;
      text += `Dr. Cristian R. Acevedo - Médico Oftalmólogo\n\n`;
      text += `*Paciente:* ${data.patient?.full_name || 'N/A'}\n`;
      text += `*Fecha:* ${formatDate(data.fecha)}\n`;
      text += `*Tipo:* ${data.tipo === 'lentes_contacto' ? 'Lentes de Contacto' : 'Anteojos'}\n\n`;
      
      text += `*OJO DERECHO (OD)*\n`;
      text += `Esfera: ${data.ojo_derecho?.esfera || '-'} | Cilindro: ${data.ojo_derecho?.cilindro || '-'} | Eje: ${data.ojo_derecho?.eje || '-'}\n`;
      if (data.ojo_derecho?.adicion) text += `Adición: ${data.ojo_derecho.adicion}\n`;
      
      text += `\n*OJO IZQUIERDO (OI)*\n`;
      text += `Esfera: ${data.ojo_izquierdo?.esfera || '-'} | Cilindro: ${data.ojo_izquierdo?.cilindro || '-'} | Eje: ${data.ojo_izquierdo?.eje || '-'}\n`;
      if (data.ojo_izquierdo?.adicion) text += `Adición: ${data.ojo_izquierdo.adicion}\n`;
      
      if (data.tipo_lente) text += `\n*Tipo de lente:* ${data.tipo_lente}`;
      if (data.material) text += `\n*Material:* ${data.material}`;
      if (data.tratamientos) text += `\n*Tratamientos:* ${data.tratamientos}`;
      if (data.observaciones) text += `\n\n*Observaciones:* ${data.observaciones}`;
      
      return text;
    } else {
      let text = `*RECETA MÉDICA*\n`;
      text += `Dr. Cristian R. Acevedo - Médico Oftalmólogo\n\n`;
      text += `*Paciente:* ${data.patient?.full_name || 'N/A'}\n`;
      text += `*Fecha:* ${formatDate(data.fecha)}\n\n`;
      text += `*MEDICAMENTOS:*\n`;
      
      data.medicamentos?.forEach((med, index) => {
        text += `\n${index + 1}. *${med.nombre}*`;
        if (med.componente_activo) text += ` (${med.componente_activo})`;
        text += `\n   _${med.modo_uso}_`;
        if (med.observaciones) text += `\n   ${med.observaciones}`;
      });
      
      if (data.observaciones_generales) {
        text += `\n\n*Observaciones:* ${data.observaciones_generales}`;
      }
      
      if (data.proxima_cita_fecha) {
        text += `\n\n*Próxima cita:* ${formatDate(data.proxima_cita_fecha)}`;
        if (data.proxima_cita_hora) text += ` a las ${data.proxima_cita_hora}`;
      }
      
      return text;
    }
  };

  const handleSendWhatsApp = (data, type) => {
    const text = generatePrescriptionText(data, type);
    const phone = data.patient?.phone?.replace(/[^0-9]/g, '') || '';
    const encodedText = encodeURIComponent(text);
    
    // Open WhatsApp with the prescription text
    const url = phone 
      ? `https://wa.me/${phone}?text=${encodedText}`
      : `https://wa.me/?text=${encodedText}`;
    
    window.open(url, '_blank');
  };

  const handleSendEmail = async (data, type) => {
    try {
      const response = await axios.post(
        `${API}/api/email/send-prescription`,
        {
          prescription_type: type,
          prescription_id: data.id,
          patient_id: data.patient_id,
          patient_email: data.patient?.email
        },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(response.data.message || 'Receta enviada por email');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Error al enviar email';
      if (errorMsg.includes('no configurado') || errorMsg.includes('API Key')) {
        toast.error('Configura primero el email en Configuración → Notificaciones Email');
      } else if (errorMsg.includes('correo')) {
        toast.error('El paciente no tiene correo registrado');
      } else {
        toast.error(errorMsg);
      }
    }
  };

  const filteredDiagnoses = diagnoses.filter(d => 
    d.name.toLowerCase().includes(diagnosisSearch.toLowerCase()) ||
    (d.category && d.category.toLowerCase().includes(diagnosisSearch.toLowerCase()))
  );

  const toggleDiagnosis = (diagId) => {
    if (newEval.diagnostico_ids.includes(diagId)) {
      setNewEval({
        ...newEval,
        diagnostico_ids: newEval.diagnostico_ids.filter(id => id !== diagId)
      });
    } else {
      setNewEval({
        ...newEval,
        diagnostico_ids: [...newEval.diagnostico_ids, diagId]
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-8" data-testid="evaluations-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(-1)} data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-800" data-testid="page-title">Evaluaciones Oftalmológicas</h1>
            <p className="text-slate-500" data-testid="patient-name">{patient?.full_name}</p>
          </div>
        </div>
        <Button onClick={() => setShowNewEvalModal(true)} className="bg-cyan-600 hover:bg-cyan-700" data-testid="new-evaluation-button">
          <Plus className="w-4 h-4 mr-2" />
          Nueva Evaluación
        </Button>
      </div>

      {/* Evaluations List */}
      {evaluations.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center" data-testid="no-evaluations-message">
            <Eye className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No hay evaluaciones registradas</p>
            <Button onClick={() => setShowNewEvalModal(true)} className="mt-4">
              Crear primera evaluación
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4" data-testid="evaluations-list">
          {evaluations.map((evaluation) => (
            <Card key={evaluation.id} className="overflow-hidden" data-testid={`evaluation-card-${evaluation.id}`}>
              <CardHeader 
                className="cursor-pointer hover:bg-slate-50 transition-colors"
                onClick={() => setExpandedEval(expandedEval === evaluation.id ? null : evaluation.id)}
                data-testid={`evaluation-header-${evaluation.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="bg-cyan-100 p-2 rounded-lg">
                      <Calendar className="w-5 h-5 text-cyan-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">
                        {new Date(evaluation.fecha + 'T12:00:00').toLocaleDateString('es-GT', { 
                          weekday: 'long', 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </CardTitle>
                      <p className="text-sm text-slate-500">{evaluation.motivo_consulta}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {evaluation.recetas_opticas_count > 0 && (
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                        <Glasses className="w-3 h-3 mr-1" />
                        {evaluation.recetas_opticas_count} Rx Óptica
                      </Badge>
                    )}
                    {evaluation.recetas_medicas_count > 0 && (
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        <Pill className="w-3 h-3 mr-1" />
                        {evaluation.recetas_medicas_count} Rx Médica
                      </Badge>
                    )}
                    {expandedEval === evaluation.id ? 
                      <ChevronUp className="w-5 h-5 text-slate-400" /> : 
                      <ChevronDown className="w-5 h-5 text-slate-400" />
                    }
                  </div>
                </div>
              </CardHeader>
              
              {expandedEval === evaluation.id && (
                <CardContent className="border-t bg-slate-50/50">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pt-4">
                    {/* Left column - Details */}
                    <div className="space-y-4">
                      {/* Diagnósticos */}
                      {evaluation.diagnosticos?.length > 0 && (
                        <div>
                          <h4 className="font-medium text-slate-700 mb-2">Diagnósticos</h4>
                          <div className="flex flex-wrap gap-2">
                            {evaluation.diagnosticos.map(dx => (
                              <Badge key={dx.id} variant="secondary">
                                {dx.name}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Tratamiento */}
                      {evaluation.tratamiento && (
                        <div>
                          <h4 className="font-medium text-slate-700 mb-1">Tratamiento</h4>
                          <p className="text-slate-600 text-sm whitespace-pre-wrap">{evaluation.tratamiento}</p>
                        </div>
                      )}
                      
                      {/* Notas */}
                      {evaluation.notas && (
                        <div>
                          <h4 className="font-medium text-slate-700 mb-1">Notas</h4>
                          <p className="text-slate-600 text-sm whitespace-pre-wrap">{evaluation.notas}</p>
                        </div>
                      )}
                      
                      {/* Actions */}
                      <div className="flex gap-2 pt-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            setCurrentEvalForRx(evaluation);
                            setShowOpticalModal(true);
                          }}
                        >
                          <Glasses className="w-4 h-4 mr-1" />
                          Receta Óptica
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            setCurrentEvalForRx(evaluation);
                            setShowMedicalModal(true);
                          }}
                        >
                          <Pill className="w-4 h-4 mr-1" />
                          Receta Médica
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteEvaluation(evaluation.id);
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Right column - Prescriptions */}
                    <div className="space-y-4">
                      {/* Optical Prescriptions */}
                      {evaluation.recetas_opticas?.length > 0 && (
                        <div>
                          <h4 className="font-medium text-slate-700 mb-2">Recetas Ópticas</h4>
                          <div className="space-y-2">
                            {evaluation.recetas_opticas.map(rx => (
                              <div key={rx.id} className="bg-white border rounded-lg p-3 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <Glasses className="w-4 h-4 text-blue-600" />
                                  <span className="text-sm capitalize">{rx.tipo.replace('_', ' ')}</span>
                                  {rx.tipo_lente && (
                                    <Badge variant="outline" className="text-xs">{rx.tipo_lente}</Badge>
                                  )}
                                </div>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button size="sm" variant="ghost" data-testid={`share-optical-rx-${rx.id}`}>
                                      <Share2 className="w-4 h-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => handlePrint({ ...rx, patient }, 'optical')} data-testid={`print-optical-rx-${rx.id}`}>
                                      <Printer className="w-4 h-4 mr-2" />
                                      Imprimir
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleSendWhatsApp({ ...rx, patient }, 'optical')} data-testid={`whatsapp-optical-rx-${rx.id}`}>
                                      <MessageCircle className="w-4 h-4 mr-2" />
                                      Enviar por WhatsApp
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleSendEmail({ ...rx, patient }, 'optical')} data-testid={`email-optical-rx-${rx.id}`}>
                                      <Mail className="w-4 h-4 mr-2" />
                                      Enviar por Email
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Medical Prescriptions */}
                      {evaluation.recetas_medicas?.length > 0 && (
                        <div>
                          <h4 className="font-medium text-slate-700 mb-2">Recetas Médicas</h4>
                          <div className="space-y-2">
                            {evaluation.recetas_medicas.map(rx => (
                              <div key={rx.id} className="bg-white border rounded-lg p-3 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <Pill className="w-4 h-4 text-green-600" />
                                  <span className="text-sm">{rx.medicamentos?.length || 0} medicamento(s)</span>
                                  {rx.proxima_cita_id && (
                                    <Badge variant="outline" className="text-xs bg-cyan-50 text-cyan-700">
                                      Próxima cita agendada
                                    </Badge>
                                  )}
                                </div>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button size="sm" variant="ghost" data-testid={`share-medical-rx-${rx.id}`}>
                                      <Share2 className="w-4 h-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => handlePrint({ ...rx, patient }, 'medical')} data-testid={`print-medical-rx-${rx.id}`}>
                                      <Printer className="w-4 h-4 mr-2" />
                                      Imprimir
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleSendWhatsApp({ ...rx, patient }, 'medical')} data-testid={`whatsapp-medical-rx-${rx.id}`}>
                                      <MessageCircle className="w-4 h-4 mr-2" />
                                      Enviar por WhatsApp
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleSendEmail({ ...rx, patient }, 'medical')} data-testid={`email-medical-rx-${rx.id}`}>
                                      <Mail className="w-4 h-4 mr-2" />
                                      Enviar por Email
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* New Evaluation Modal */}
      <Dialog open={showNewEvalModal} onOpenChange={setShowNewEvalModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nueva Evaluación Oftalmológica</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* 1. Fecha y Edad */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Fecha *</label>
                <Input
                  type="date"
                  value={newEval.fecha}
                  onChange={(e) => setNewEval({ ...newEval, fecha: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Edad del Paciente</label>
                <div className="h-10 px-3 py-2 bg-slate-100 rounded-md flex items-center text-slate-700">
                  {patient?.birth_date ? (
                    <span className="font-medium">{calculateAge(patient.birth_date)} años</span>
                  ) : (
                    <span className="text-slate-400 text-sm">Sin fecha de nacimiento registrada</span>
                  )}
                </div>
              </div>
            </div>
            
            {/* 2. Motivo de Consulta y Hallazgos */}
            <div>
              <label className="text-sm font-medium text-slate-700">Motivo de Consulta *</label>
              <Textarea
                value={newEval.motivo_consulta}
                onChange={(e) => setNewEval({ ...newEval, motivo_consulta: e.target.value })}
                placeholder="Describe el motivo de la consulta..."
                rows={2}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-slate-700">Hallazgos</label>
              <Textarea
                value={newEval.hallazgos}
                onChange={(e) => setNewEval({ ...newEval, hallazgos: e.target.value })}
                placeholder="Describe los hallazgos del examen..."
                rows={3}
              />
            </div>
            
            {/* 3. Tratamiento */}
            <div>
              <label className="text-sm font-medium text-slate-700">Tratamiento</label>
              <Textarea
                value={newEval.tratamiento}
                onChange={(e) => setNewEval({ ...newEval, tratamiento: e.target.value })}
                placeholder="Describe el tratamiento indicado..."
                rows={2}
              />
            </div>
            
            {/* 4. Diagnósticos */}
            <div>
              <label className="text-sm font-medium text-slate-700">Diagnósticos</label>
              <div className="mt-2 space-y-2">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Buscar diagnósticos..."
                    value={diagnosisSearch}
                    onChange={(e) => setDiagnosisSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>
                
                {/* Selected diagnoses */}
                {newEval.diagnostico_ids.length > 0 && (
                  <div className="flex flex-wrap gap-2 p-2 bg-slate-50 rounded-lg">
                    {newEval.diagnostico_ids.map(id => {
                      const dx = diagnoses.find(d => d.id === id);
                      return dx ? (
                        <Badge 
                          key={id} 
                          className="cursor-pointer bg-cyan-100 text-cyan-800 hover:bg-cyan-200"
                          onClick={() => toggleDiagnosis(id)}
                        >
                          {dx.name}
                          <X className="w-3 h-3 ml-1" />
                        </Badge>
                      ) : null;
                    })}
                  </div>
                )}
                
                {/* Available diagnoses */}
                <div className="max-h-32 overflow-y-auto border rounded-lg">
                  {filteredDiagnoses.slice(0, 15).map(dx => (
                    <div
                      key={dx.id}
                      className={`px-3 py-2 cursor-pointer hover:bg-slate-50 flex items-center justify-between ${
                        newEval.diagnostico_ids.includes(dx.id) ? 'bg-cyan-50' : ''
                      }`}
                      onClick={() => toggleDiagnosis(dx.id)}
                    >
                      <span className="text-sm">{dx.name}</span>
                      {dx.category && (
                        <Badge variant="outline" className="text-xs">{dx.category}</Badge>
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Create new diagnosis */}
                {!showNewDiagnosisInput ? (
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowNewDiagnosisInput(true)}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Nuevo diagnóstico
                  </Button>
                ) : (
                  <div className="flex gap-2 items-end">
                    <div className="flex-1">
                      <Input
                        placeholder="Nombre del diagnóstico"
                        value={newDiagnosis.name}
                        onChange={(e) => setNewDiagnosis({ ...newDiagnosis, name: e.target.value })}
                      />
                    </div>
                    <div className="w-40">
                      <Select
                        value={newDiagnosis.category}
                        onValueChange={(value) => setNewDiagnosis({ ...newDiagnosis, category: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Categoría" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Refractivo">Refractivo</SelectItem>
                          <SelectItem value="Superficie ocular">Superficie ocular</SelectItem>
                          <SelectItem value="Cristalino">Cristalino</SelectItem>
                          <SelectItem value="Glaucoma">Glaucoma</SelectItem>
                          <SelectItem value="Retina">Retina</SelectItem>
                          <SelectItem value="Otro">Otro</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button type="button" size="sm" onClick={handleCreateDiagnosis}>
                      Agregar
                    </Button>
                    <Button 
                      type="button" 
                      size="sm" 
                      variant="ghost"
                      onClick={() => {
                        setShowNewDiagnosisInput(false);
                        setNewDiagnosis({ name: '', category: '' });
                      }}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
            
            {/* 5. Notas adicionales */}
            <div>
              <label className="text-sm font-medium text-slate-700">Notas adicionales</label>
              <Textarea
                value={newEval.notas}
                onChange={(e) => setNewEval({ ...newEval, notas: e.target.value })}
                placeholder="Notas u observaciones..."
                rows={2}
              />
            </div>
            
            {/* 6. Agregar a CRM Cirugías */}
            <div className="border rounded-lg p-4 bg-amber-50/50">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="agregar-crm"
                  checked={newEval.agregar_crm_cirugia}
                  onCheckedChange={(checked) => setNewEval({ ...newEval, agregar_crm_cirugia: checked })}
                />
                <div className="flex-1">
                  <label htmlFor="agregar-crm" className="text-sm font-medium text-slate-700 cursor-pointer flex items-center gap-2">
                    <UserPlus className="w-4 h-4 text-amber-600" />
                    Agregar a lista de interesados en cirugía
                  </label>
                  
                  {newEval.agregar_crm_cirugia && (
                    <div className="mt-3 grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-slate-500">Tipo de Cirugía *</label>
                        <Select
                          value={newEval.tipo_cirugia_id}
                          onValueChange={(value) => setNewEval({ ...newEval, tipo_cirugia_id: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Seleccionar..." />
                          </SelectTrigger>
                          <SelectContent>
                            {surgeryTypes.map(type => (
                              <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-xs text-slate-500">Nivel de Interés</label>
                        <Select
                          value={newEval.nivel_interes}
                          onValueChange={(value) => setNewEval({ ...newEval, nivel_interes: value })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="bajo">Bajo - Informativo</SelectItem>
                            <SelectItem value="medio">Medio - Considerando</SelectItem>
                            <SelectItem value="alto">Alto - Decidido</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* 7. Botones de acción */}
            <div className="border-t pt-4 space-y-3">
              <div className="flex justify-between items-center">
                <div className="flex gap-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    disabled={savingEval}
                    onClick={async () => {
                      await handleCreateEvaluation();
                      if (lastCreatedEvalId) {
                        setShowOpticalModal(true);
                      }
                    }}
                  >
                    <Glasses className="w-4 h-4 mr-1" />
                    Guardar + Receta Lentes
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    disabled={savingEval}
                    onClick={async () => {
                      await handleCreateEvaluation();
                      if (lastCreatedEvalId) {
                        setShowMedicalModal(true);
                      }
                    }}
                  >
                    <Pill className="w-4 h-4 mr-1" />
                    Guardar + Receta Médica
                  </Button>
                </div>
              </div>
              
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowNewEvalModal(false)}>
                  Cancelar
                </Button>
                <Button 
                  onClick={handleCreateEvaluation} 
                  className="bg-cyan-600 hover:bg-cyan-700"
                  disabled={savingEval}
                >
                  {savingEval ? 'Guardando...' : 'Guardar Evaluación'}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Optical Prescription Modal */}
      <Dialog open={showOpticalModal} onOpenChange={setShowOpticalModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nueva Receta Óptica</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="text-sm font-medium text-slate-700">Tipo</label>
                <Select
                  value={opticalRx.tipo}
                  onValueChange={(value) => setOpticalRx({ ...opticalRx, tipo: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="anteojos">Anteojos</SelectItem>
                    <SelectItem value="lentes_contacto">Lentes de Contacto</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <label className="text-sm font-medium text-slate-700">Tipo de Lente</label>
                <Select
                  value={opticalRx.tipo_lente}
                  onValueChange={(value) => setOpticalRx({ ...opticalRx, tipo_lente: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monofocal">Monofocal</SelectItem>
                    <SelectItem value="bifocal">Bifocal</SelectItem>
                    <SelectItem value="progresivo">Progresivo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Eyes grid */}
            <div className="grid grid-cols-2 gap-4">
              {/* Right Eye */}
              <div className="border rounded-lg p-4">
                <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                  <Eye className="w-4 h-4" /> Ojo Derecho (OD)
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-500">Esfera</label>
                    <Input
                      placeholder="+/- 0.00"
                      value={opticalRx.ojo_derecho.esfera}
                      onChange={(e) => setOpticalRx({
                        ...opticalRx,
                        ojo_derecho: { ...opticalRx.ojo_derecho, esfera: e.target.value }
                      })}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Cilindro</label>
                    <Input
                      placeholder="-0.00"
                      value={opticalRx.ojo_derecho.cilindro}
                      onChange={(e) => setOpticalRx({
                        ...opticalRx,
                        ojo_derecho: { ...opticalRx.ojo_derecho, cilindro: e.target.value }
                      })}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Eje</label>
                    <Input
                      placeholder="0°"
                      value={opticalRx.ojo_derecho.eje}
                      onChange={(e) => setOpticalRx({
                        ...opticalRx,
                        ojo_derecho: { ...opticalRx.ojo_derecho, eje: e.target.value }
                      })}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Adición</label>
                    <Input
                      placeholder="+0.00"
                      value={opticalRx.ojo_derecho.adicion}
                      onChange={(e) => setOpticalRx({
                        ...opticalRx,
                        ojo_derecho: { ...opticalRx.ojo_derecho, adicion: e.target.value }
                      })}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">DP</label>
                    <Input
                      placeholder="mm"
                      value={opticalRx.ojo_derecho.dp}
                      onChange={(e) => setOpticalRx({
                        ...opticalRx,
                        ojo_derecho: { ...opticalRx.ojo_derecho, dp: e.target.value }
                      })}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">AV s/c</label>
                    <Input
                      placeholder="20/20"
                      value={opticalRx.ojo_derecho.av_sin_correccion}
                      onChange={(e) => setOpticalRx({
                        ...opticalRx,
                        ojo_derecho: { ...opticalRx.ojo_derecho, av_sin_correccion: e.target.value }
                      })}
                    />
                  </div>
                </div>
              </div>

              {/* Left Eye */}
              <div className="border rounded-lg p-4">
                <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                  <Eye className="w-4 h-4" /> Ojo Izquierdo (OI)
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-500">Esfera</label>
                    <Input
                      placeholder="+/- 0.00"
                      value={opticalRx.ojo_izquierdo.esfera}
                      onChange={(e) => setOpticalRx({
                        ...opticalRx,
                        ojo_izquierdo: { ...opticalRx.ojo_izquierdo, esfera: e.target.value }
                      })}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Cilindro</label>
                    <Input
                      placeholder="-0.00"
                      value={opticalRx.ojo_izquierdo.cilindro}
                      onChange={(e) => setOpticalRx({
                        ...opticalRx,
                        ojo_izquierdo: { ...opticalRx.ojo_izquierdo, cilindro: e.target.value }
                      })}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Eje</label>
                    <Input
                      placeholder="0°"
                      value={opticalRx.ojo_izquierdo.eje}
                      onChange={(e) => setOpticalRx({
                        ...opticalRx,
                        ojo_izquierdo: { ...opticalRx.ojo_izquierdo, eje: e.target.value }
                      })}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Adición</label>
                    <Input
                      placeholder="+0.00"
                      value={opticalRx.ojo_izquierdo.adicion}
                      onChange={(e) => setOpticalRx({
                        ...opticalRx,
                        ojo_izquierdo: { ...opticalRx.ojo_izquierdo, adicion: e.target.value }
                      })}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">DP</label>
                    <Input
                      placeholder="mm"
                      value={opticalRx.ojo_izquierdo.dp}
                      onChange={(e) => setOpticalRx({
                        ...opticalRx,
                        ojo_izquierdo: { ...opticalRx.ojo_izquierdo, dp: e.target.value }
                      })}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">AV s/c</label>
                    <Input
                      placeholder="20/20"
                      value={opticalRx.ojo_izquierdo.av_sin_correccion}
                      onChange={(e) => setOpticalRx({
                        ...opticalRx,
                        ojo_izquierdo: { ...opticalRx.ojo_izquierdo, av_sin_correccion: e.target.value }
                      })}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Material</label>
                <Select
                  value={opticalRx.material}
                  onValueChange={(value) => setOpticalRx({ ...opticalRx, material: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CR-39">CR-39</SelectItem>
                    <SelectItem value="Policarbonato">Policarbonato</SelectItem>
                    <SelectItem value="Hi-Index 1.67">Hi-Index 1.67</SelectItem>
                    <SelectItem value="Hi-Index 1.74">Hi-Index 1.74</SelectItem>
                    <SelectItem value="Trivex">Trivex</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Tratamientos</label>
                <Input
                  placeholder="Antireflejo, filtro azul, fotocromático..."
                  value={opticalRx.tratamientos}
                  onChange={(e) => setOpticalRx({ ...opticalRx, tratamientos: e.target.value })}
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700">Observaciones</label>
              <Textarea
                value={opticalRx.observaciones}
                onChange={(e) => setOpticalRx({ ...opticalRx, observaciones: e.target.value })}
                placeholder="Observaciones adicionales..."
                rows={2}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => { setShowOpticalModal(false); resetOpticalRx(); }}>
                Cancelar
              </Button>
              <Button onClick={handleCreateOpticalRx} className="bg-cyan-600 hover:bg-cyan-700">
                Guardar Receta
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Medical Prescription Modal */}
      <Dialog open={showMedicalModal} onOpenChange={setShowMedicalModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nueva Receta Médica</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Medicamentos */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-slate-700">Medicamentos</label>
                <Button type="button" size="sm" variant="outline" onClick={addMedicamento}>
                  <Plus className="w-4 h-4 mr-1" />
                  Agregar
                </Button>
              </div>
              
              <div className="space-y-3">
                {medicalRx.medicamentos.map((med, index) => (
                  <div key={index} className="border rounded-lg p-3 bg-slate-50">
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-sm font-medium text-slate-600">Medicamento {index + 1}</span>
                      {medicalRx.medicamentos.length > 1 && (
                        <Button 
                          type="button" 
                          size="sm" 
                          variant="ghost" 
                          className="text-red-600 h-6 w-6 p-0"
                          onClick={() => removeMedicamento(index)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-slate-500">Nombre *</label>
                        <Input
                          placeholder="Nombre del medicamento"
                          value={med.nombre}
                          onChange={(e) => updateMedicamento(index, 'nombre', e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-500">Componente activo</label>
                        <Input
                          placeholder="Componente activo"
                          value={med.componente_activo}
                          onChange={(e) => updateMedicamento(index, 'componente_activo', e.target.value)}
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="text-xs text-slate-500">Modo de uso *</label>
                        <Textarea
                          placeholder="Ej: 1 gota cada 8 horas en ambos ojos por 7 días"
                          value={med.modo_uso}
                          onChange={(e) => updateMedicamento(index, 'modo_uso', e.target.value)}
                          rows={2}
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="text-xs text-slate-500">Observaciones</label>
                        <Input
                          placeholder="Observaciones del medicamento"
                          value={med.observaciones}
                          onChange={(e) => updateMedicamento(index, 'observaciones', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Próxima cita */}
            <div className="border rounded-lg p-4 bg-cyan-50/50">
              <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Agendar Próxima Cita (opcional)
              </h4>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs text-slate-500">Fecha</label>
                  <Input
                    type="date"
                    value={medicalRx.proxima_cita_fecha}
                    onChange={(e) => setMedicalRx({ ...medicalRx, proxima_cita_fecha: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500">Hora</label>
                  <Input
                    type="time"
                    value={medicalRx.proxima_cita_hora}
                    onChange={(e) => setMedicalRx({ ...medicalRx, proxima_cita_hora: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500">Tipo</label>
                  <Select
                    value={medicalRx.proxima_cita_tipo}
                    onValueChange={(value) => setMedicalRx({ ...medicalRx, proxima_cita_tipo: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="control">Control</SelectItem>
                      <SelectItem value="seguimiento">Seguimiento</SelectItem>
                      <SelectItem value="post-operatorio">Post-operatorio</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700">Observaciones generales</label>
              <Textarea
                value={medicalRx.observaciones_generales}
                onChange={(e) => setMedicalRx({ ...medicalRx, observaciones_generales: e.target.value })}
                placeholder="Observaciones generales de la receta..."
                rows={2}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => { setShowMedicalModal(false); resetMedicalRx(); }}>
                Cancelar
              </Button>
              <Button onClick={handleCreateMedicalRx} className="bg-cyan-600 hover:bg-cyan-700">
                Guardar Receta
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Print Preview Modal */}
      <Dialog open={showPrintPreview} onOpenChange={setShowPrintPreview}>
        <DialogContent className="max-w-2xl print:max-w-none print:w-full print:h-full print:m-0 print:p-0">
          <DialogHeader className="print:hidden">
            <DialogTitle>Vista previa de impresión</DialogTitle>
          </DialogHeader>
          
          {printData && printType === 'optical' && (
            <div className="p-6 print:p-8" id="print-content">
              <div className="text-center mb-6">
                <h1 className="text-xl font-bold">Dr. Cristian R. Acevedo</h1>
                <p className="text-sm text-slate-600">Médico Oftalmólogo</p>
                <p className="text-xs text-slate-500">Colegiado No. XXXXX</p>
              </div>
              
              <div className="border-b pb-4 mb-4">
                <p className="text-sm"><strong>Paciente:</strong> {printData.patient?.full_name}</p>
                <p className="text-sm"><strong>Fecha:</strong> {new Date(printData.fecha + 'T12:00:00').toLocaleDateString('es-GT')}</p>
              </div>
              
              <h2 className="text-lg font-semibold mb-4 text-center">
                RECETA ÓPTICA - {printData.tipo === 'lentes_contacto' ? 'Lentes de Contacto' : 'Anteojos'}
              </h2>
              
              <table className="w-full border-collapse border mb-4 text-sm">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="border p-2">Ojo</th>
                    <th className="border p-2">Esfera</th>
                    <th className="border p-2">Cilindro</th>
                    <th className="border p-2">Eje</th>
                    <th className="border p-2">Adición</th>
                    <th className="border p-2">DP</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border p-2 font-medium">OD</td>
                    <td className="border p-2 text-center">{printData.ojo_derecho?.esfera || '-'}</td>
                    <td className="border p-2 text-center">{printData.ojo_derecho?.cilindro || '-'}</td>
                    <td className="border p-2 text-center">{printData.ojo_derecho?.eje || '-'}</td>
                    <td className="border p-2 text-center">{printData.ojo_derecho?.adicion || '-'}</td>
                    <td className="border p-2 text-center">{printData.ojo_derecho?.dp || '-'}</td>
                  </tr>
                  <tr>
                    <td className="border p-2 font-medium">OI</td>
                    <td className="border p-2 text-center">{printData.ojo_izquierdo?.esfera || '-'}</td>
                    <td className="border p-2 text-center">{printData.ojo_izquierdo?.cilindro || '-'}</td>
                    <td className="border p-2 text-center">{printData.ojo_izquierdo?.eje || '-'}</td>
                    <td className="border p-2 text-center">{printData.ojo_izquierdo?.adicion || '-'}</td>
                    <td className="border p-2 text-center">{printData.ojo_izquierdo?.dp || '-'}</td>
                  </tr>
                </tbody>
              </table>
              
              {(printData.tipo_lente || printData.material || printData.tratamientos) && (
                <div className="mb-4 text-sm">
                  {printData.tipo_lente && <p><strong>Tipo de lente:</strong> {printData.tipo_lente}</p>}
                  {printData.material && <p><strong>Material:</strong> {printData.material}</p>}
                  {printData.tratamientos && <p><strong>Tratamientos:</strong> {printData.tratamientos}</p>}
                </div>
              )}
              
              {printData.observaciones && (
                <div className="mb-4 text-sm">
                  <p><strong>Observaciones:</strong> {printData.observaciones}</p>
                </div>
              )}
              
              <div className="mt-12 pt-4 border-t text-center">
                <div className="w-48 mx-auto border-t border-black pt-2">
                  <p className="text-sm font-medium">Dr. Cristian R. Acevedo</p>
                  <p className="text-xs text-slate-600">Médico Oftalmólogo</p>
                </div>
              </div>
            </div>
          )}
          
          {printData && printType === 'medical' && (
            <div className="p-6 print:p-8" id="print-content">
              <div className="text-center mb-6">
                <h1 className="text-xl font-bold">Dr. Cristian R. Acevedo</h1>
                <p className="text-sm text-slate-600">Médico Oftalmólogo</p>
                <p className="text-xs text-slate-500">Colegiado No. XXXXX</p>
              </div>
              
              <div className="border-b pb-4 mb-4">
                <p className="text-sm"><strong>Paciente:</strong> {printData.patient?.full_name}</p>
                <p className="text-sm"><strong>Fecha:</strong> {new Date(printData.fecha + 'T12:00:00').toLocaleDateString('es-GT')}</p>
              </div>
              
              <h2 className="text-lg font-semibold mb-4 text-center">RECETA MÉDICA</h2>
              
              <div className="space-y-4 mb-6">
                {printData.medicamentos?.map((med, index) => (
                  <div key={index} className="border-b pb-3">
                    <p className="font-medium">{index + 1}. {med.nombre}</p>
                    {med.componente_activo && (
                      <p className="text-sm text-slate-600 ml-4">({med.componente_activo})</p>
                    )}
                    <p className="text-sm ml-4"><strong>Indicación:</strong> {med.modo_uso}</p>
                    {med.observaciones && (
                      <p className="text-sm ml-4 text-slate-600"><em>{med.observaciones}</em></p>
                    )}
                  </div>
                ))}
              </div>
              
              {printData.observaciones_generales && (
                <div className="mb-4 text-sm bg-slate-50 p-3 rounded">
                  <p><strong>Observaciones:</strong> {printData.observaciones_generales}</p>
                </div>
              )}
              
              {printData.proxima_cita_fecha && (
                <div className="mb-4 text-sm bg-cyan-50 p-3 rounded">
                  <p><strong>Próxima cita:</strong> <span style={{ fontSize: '1.5em', fontWeight: 700 }}>{new Date(printData.proxima_cita_fecha + 'T12:00:00').toLocaleDateString('es-GT', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span> {printData.proxima_cita_hora && `a las ${printData.proxima_cita_hora}`}</p>
                </div>
              )}
              
              <div className="mt-12 pt-4 border-t text-center">
                <div className="w-48 mx-auto border-t border-black pt-2">
                  <p className="text-sm font-medium">Dr. Cristian R. Acevedo</p>
                  <p className="text-xs text-slate-600">Médico Oftalmólogo</p>
                </div>
              </div>
            </div>
          )}
          
          <div className="flex justify-end gap-2 print:hidden">
            <Button variant="outline" onClick={() => setShowPrintPreview(false)}>
              Cerrar
            </Button>
            <Button onClick={executePrint} className="bg-cyan-600 hover:bg-cyan-700">
              <Printer className="w-4 h-4 mr-2" />
              Imprimir
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Print styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #print-content, #print-content * {
            visibility: visible;
          }
          #print-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default PatientEvaluationsPage;
